import React, { useEffect } from "react";

import Carousel from "components/botanic/Carousel";
import TrackImpression from "components/TrackImpression";

import { Product } from "../../../custom-types/Product";
import ProductCard from "../../brands/ProductCatalogSection/ProductCard";
import { CAROUSEL_ROUTE_MAP } from "../LandingPage";
import { GTM_EVENTS, trackGtmEvent } from "../lib/gtm";

export type LandingPageCarouselRoute =
  | (typeof CAROUSEL_ROUTE_MAP)[keyof typeof CAROUSEL_ROUTE_MAP]
  | string;

export type ProductsCarouselProps = {
  carouselPath?: LandingPageCarouselRoute;
  header: string | JSX.Element;
  products: Product[];
  referringPage?: string;
  seeMoreText?: string;
  onLoad?: () => void;
  seeMoreOnClick?: () => void;
  productCardOnClick?: () => void;
};

export const ProductsCarousel: React.FC<ProductsCarouselProps> = ({
  carouselPath,
  header,
  products,
  referringPage,
  seeMoreText,
  onLoad,
  seeMoreOnClick,
  productCardOnClick,
}) => {
  const carouselSlug =
    carouselPath && carouselPath.split("/")[carouselPath.split("/").length - 1];

  useEffect(() => {
    onLoad && onLoad();
  }, []);

  if (!products || products.length === 0) {
    return null;
  }

  return (
    <Carousel
      title={header}
      seeMoreHref={carouselPath}
      seeMoreText={seeMoreText}
      seeMoreOnClick={() => {
        seeMoreOnClick
          ? seeMoreOnClick()
          : trackGtmEvent(GTM_EVENTS.PRODUCT.CAROUSEL_SEE_MORE_CLICK, {
              eventLabel: carouselPath,
            });
      }}
      className={referringPage !== "brand" ? "my-section" : ""}
    >
      {products.map((product) => {
        const eventLabel = carouselSlug
          ? `${carouselSlug} product card`
          : `/products/details/${product.slug}`;
        const dimensions = {
          brandId: product.brand.id,
          productId: product.id,
        };
        const dynamicGtmEventData = {
          dimensions,
          eventLabel,
        };

        return (
          <TrackImpression
            category={GTM_EVENTS.PRODUCT.CARD_IMPRESSION.eventCategory}
            className="h-full"
            customDimensions={dimensions}
            key={product.slug}
            label={eventLabel}
          >
            <ProductCard
              data-testid="product-card"
              product={product}
              onClick={() => {
                productCardOnClick
                  ? productCardOnClick()
                  : trackGtmEvent(
                      GTM_EVENTS.PRODUCT.CARD_CLICK,
                      dynamicGtmEventData
                    );
              }}
              className="h-full"
            />
          </TrackImpression>
        );
      })}
    </Carousel>
  );
};
